import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { useActualizarPlantilla, useCrearPlantilla } from "@/hooks/usePlantillas";
import { getApiErrorMessage, parseValidationErrors } from "@/lib/api";
import type { DatosDePlantilla, ResumenDePlantilla } from "@/lib/plantillas";

interface CamposProps {
  nombre: string;
  proposito: string;
  valor?: string;
  errorDeNombre: string | null;
  onNombre: (v: string) => void;
  onProposito: (v: string) => void;
  onValor?: (v: string) => void;
}

function CamposDePlantilla({
  nombre,
  proposito,
  valor,
  errorDeNombre,
  onNombre,
  onProposito,
  onValor,
}: CamposProps) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="plantilla-nombre">Nombre</Label>
        <Input
          id="plantilla-nombre"
          value={nombre}
          onChange={(e) => onNombre(e.target.value)}
          aria-invalid={Boolean(errorDeNombre)}
          placeholder="Ej: Tasación agrícola completa"
          autoFocus
        />
        {errorDeNombre ? <p className="text-destructive text-xs">{errorDeNombre}</p> : null}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="plantilla-proposito">Propósito / cuándo usar</Label>
        <Textarea
          id="plantilla-proposito"
          value={proposito}
          onChange={(e) => onProposito(e.target.value)}
          rows={3}
        />
      </div>

      {onValor ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="plantilla-valor">Valor que entrega</Label>
          <Textarea
            id="plantilla-valor"
            value={valor ?? ""}
            onChange={(e) => onValor(e.target.value)}
            rows={2}
          />
        </div>
      ) : null}
    </div>
  );
}

function errorDelNombre(error: unknown): string | null {
  return parseValidationErrors(error)?.nombre ?? null;
}

/* ------------------------------------------------------------------ */

interface NuevaPlantillaDialogProps {
  abierto: boolean;
  plantillas: ResumenDePlantilla[];
  onCerrar: () => void;
  /** Recibe el id de la plantilla creada: la pantalla navega a su editor. */
  onCreada: (id: number) => void;
}

/**
 * "Nueva plantilla": parte de otra (copia su estructura) o de cero. El
 * propósito queda vacío si no se escribe uno; no se hereda en silencio.
 */
export function NuevaPlantillaDialog(props: NuevaPlantillaDialogProps) {
  /* El `key` lo pone quien monta: remontar al abrir deja el formulario limpio
     sin un efecto que lo resetee. */
  return props.abierto ? <ContenidoNueva {...props} /> : null;
}

function ContenidoNueva({ plantillas, onCerrar, onCreada }: NuevaPlantillaDialogProps) {
  const crear = useCrearPlantilla();
  const [nombre, setNombre] = useState("");
  const [proposito, setProposito] = useState("");
  const [base, setBase] = useState("");
  const [error, setError] = useState<string | null>(null);

  const enviar = () => {
    if (!nombre.trim()) {
      setError("El nombre de la plantilla es obligatorio.");
      return;
    }
    setError(null);
    crear.mutate(
      {
        nombre: nombre.trim(),
        proposito: proposito.trim() || null,
        ...(base ? { base_id: Number(base) } : {}),
      },
      {
        onSuccess: (detalle) => {
          toast.success(`Plantilla «${detalle.plantilla.nombre}» creada`);
          onCreada(detalle.plantilla.id);
        },
        onError: (e) => {
          const mensaje = errorDelNombre(e);
          if (mensaje) setError(mensaje);
          else toast.error(getApiErrorMessage(e, "No pudimos crear la plantilla. Inténtalo de nuevo."));
        },
      },
    );
  };

  return (
    <Dialog open onOpenChange={(abierto) => !abierto && !crear.isPending && onCerrar()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Nueva plantilla</DialogTitle>
          <DialogDescription>
            Parte de una plantilla existente o de una vacía. Después puedes cambiar sus grupos
            e ítems.
          </DialogDescription>
        </DialogHeader>

        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            enviar();
          }}
          className="flex flex-col gap-4"
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="plantilla-base">Partir desde</Label>
            <NativeSelect id="plantilla-base" value={base} onChange={(e) => setBase(e.target.value)}>
              <option value="">Plantilla vacía</option>
              {plantillas.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
            </NativeSelect>
          </div>

          <CamposDePlantilla
            nombre={nombre}
            proposito={proposito}
            errorDeNombre={error}
            onNombre={setNombre}
            onProposito={setProposito}
          />

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCerrar} disabled={crear.isPending}>
              Cancelar
            </Button>
            <Button type="submit" disabled={crear.isPending}>
              {crear.isPending ? <Spinner /> : null}
              Crear plantilla
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */

interface EditarDatosDialogProps {
  abierto: boolean;
  plantilla: DatosDePlantilla;
  onCerrar: () => void;
}

/**
 * "Editar datos": nombre, propósito y valor que entrega. Se guarda al
 * instante con su propio PUT — no pasa por el borrador de la estructura,
 * porque no es parte de ella.
 */
export function EditarDatosDialog(props: EditarDatosDialogProps) {
  return props.abierto ? <ContenidoEditar {...props} /> : null;
}

function ContenidoEditar({ plantilla, onCerrar }: EditarDatosDialogProps) {
  const actualizar = useActualizarPlantilla(plantilla.id);
  const [nombre, setNombre] = useState(plantilla.nombre);
  const [proposito, setProposito] = useState(plantilla.proposito ?? "");
  const [valor, setValor] = useState(plantilla.valor ?? "");
  const [error, setError] = useState<string | null>(null);

  const enviar = () => {
    if (!nombre.trim()) {
      setError("El nombre de la plantilla es obligatorio.");
      return;
    }
    setError(null);
    actualizar.mutate(
      { nombre: nombre.trim(), proposito: proposito.trim() || null, valor: valor.trim() || null },
      {
        onSuccess: () => {
          toast.success("Datos de la plantilla actualizados");
          onCerrar();
        },
        onError: (e) => {
          const mensaje = errorDelNombre(e);
          if (mensaje) setError(mensaje);
          else toast.error(getApiErrorMessage(e, "No pudimos guardar los datos. Inténtalo de nuevo."));
        },
      },
    );
  };

  return (
    <Dialog open onOpenChange={(abierto) => !abierto && !actualizar.isPending && onCerrar()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Datos de la plantilla</DialogTitle>
          <DialogDescription>
            Se guardan al instante; no dependen de los cambios sin guardar de la estructura.
          </DialogDescription>
        </DialogHeader>

        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            enviar();
          }}
          className="flex flex-col gap-4"
        >
          <CamposDePlantilla
            nombre={nombre}
            proposito={proposito}
            valor={valor}
            errorDeNombre={error}
            onNombre={setNombre}
            onProposito={setProposito}
            onValor={setValor}
          />

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onCerrar}
              disabled={actualizar.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={actualizar.isPending}>
              {actualizar.isPending ? <Spinner /> : null}
              Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
